// Procedural ambient music — sine/triangle pad, 4-chord loop, softly filtered.
// Designed to be atmospheric and unobtrusive; no external assets.

let _ctx = null;
let _master = null;
let _playing = false;
let _timer = null;
let _stopHandles = [];
let _volume = 0.18;

const PROG = [
  // Am9 -> Fmaj7 -> Cmaj7 -> G9  (in Hz, three-note voicings)
  [220.00, 261.63, 329.63],
  [174.61, 261.63, 329.63],
  [261.63, 329.63, 392.00],
  [196.00, 293.66, 369.99],
];
const CHORD_MS = 6000; // 6s per chord = ~10 BPM per bar, calm

function ensureCtx() {
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  _ctx = new Ctx();
  const filter = _ctx.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = 900;
  filter.Q.value = 0.7;
  _master = _ctx.createGain();
  _master.gain.value = _volume;
  filter.connect(_master);
  _master.connect(_ctx.destination);
  _master._filterInput = filter;
  return _ctx;
}

function scheduleChord(chord) {
  const ctx = _ctx;
  const input = _master._filterInput;
  const now = ctx.currentTime;
  const attack = 1.2;
  const release = 1.5;
  const hold = CHORD_MS / 1000 - attack - release;

  chord.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const oscSub = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    oscSub.type = "triangle";
    osc.frequency.value = freq;
    oscSub.frequency.value = freq / 2;
    gain.gain.value = 0;
    osc.connect(gain);
    oscSub.connect(gain);
    gain.connect(input);
    const peak = 0.22 - i * 0.04;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(peak, now + attack);
    gain.gain.setValueAtTime(peak, now + attack + hold);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + attack + hold + release);
    osc.start(now);
    oscSub.start(now);
    osc.stop(now + attack + hold + release + 0.1);
    oscSub.stop(now + attack + hold + release + 0.1);
    _stopHandles.push(osc, oscSub);
  });
}

export async function startMusic() {
  const ctx = ensureCtx();
  if (!ctx) return false;
  if (ctx.state === "suspended") await ctx.resume();
  if (_playing) return true;
  _playing = true;
  let i = 0;
  const tick = () => {
    if (!_playing) return;
    scheduleChord(PROG[i % PROG.length]);
    i++;
    _timer = setTimeout(tick, CHORD_MS);
  };
  tick();
  return true;
}

export function stopMusic() {
  _playing = false;
  if (_timer) { clearTimeout(_timer); _timer = null; }
  if (_master) {
    const ctx = _ctx;
    _master.gain.cancelScheduledValues(ctx.currentTime);
    _master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.4);
  }
  _stopHandles.forEach((o) => { try { o.stop(); } catch { /* ignore */ } });
  _stopHandles = [];
}

export function setVolume(v) {
  _volume = Math.max(0, Math.min(1, v));
  if (_master && _ctx) {
    _master.gain.setTargetAtTime(_volume, _ctx.currentTime, 0.05);
  }
}

export function getVolume() { return _volume; }
export function isPlaying() { return _playing; }
